<script context="module">
	import { z } from "zod";
	export const name = "default view";

	export const CustomFields = z.object({
		show_date: z.boolean().default(true),
	});
</script>

<script lang="ts">
	import Data from "@reperio-news/core.components/blocks/ContentItem/Data";
	import { stripTags } from "@reperio-news/core.components/includes/Helpers";
	type Data = z.infer<typeof Data>;
	import { getContext } from "svelte";
	import ImageView from "@reperio-news/core.components/ImageView.svelte";
	import { formatRo } from "@reperio-news/core.components/dates.js";

	export let data: Data = {};
	const c: SvelteStore<number | null> = getContext("parentBlockColumnCount");
	const b: SvelteStore<string | null> = getContext(
		"parentBlockBackgroundColor"
	);
</script>

{#if data && data.content}
	<div
		class="entry co-{$c}"
		class:withBackgroundColor={$b}
		style="--parentColumns:{$c};"
	>
		{#if data.content.poster}
			<a
				class="image"
				href={data.content.url}
				title={data.content.summary}
			>
				<ImageView
					src={data.content.poster}
					crop={data.content.poster.crop}
					width={$c === 1 ? 900 : 700}
					height={$c === 1 ? 400 : 420}
					widths={$c === 1 ? [900, 720, 600, 450] : [700, 500]}
					heights={$c === 1 ? [400, 320, 267, 200] : [420, 300]}
					animatedQuality={45}
					fit="cover"
				/>
			</a>
		{/if}

		<div class="mainInfo">
			<div class="of">
				{#if data.content.topic}
					<a
						class="topic"
						href="//{data.content.domain}/{data.content.topic.slug}"
						title="Ultimele știri - {data.content.topic.name}"
					>
						#{data.content.topic.name}
					</a>
				{/if}
			</div>

			{#if data.custom_fields?.show_date !== false && data.content.published_at}
				<div class="date">
					{formatRo(data.content.published_at, "DD MMMM YYYY")}
				</div>
			{/if}

			<a
				class="title"
				href={data.content.url}
				title={stripTags(data.content.summary)}
				>{@html stripTags(data.content.title, ["i", "b", "strong", "font"])}</a
			>
		</div>
	</div>
{/if}

<style lang="scss">
	.co-1 {
		flex-grow: 0 !important;
		width: calc(100% - 20px);
		.mainInfo a.title {
			padding: 5px;
			text-align: left;
			-webkit-line-clamp: 10;
		}
	}

	.co-2 {
		width: calc(50% - 30px);
		.mainInfo .title {
			-webkit-line-clamp: 3;
		}
	}

	.co-3 {
		width: calc(33% - 30px);
		.mainInfo .title {
			-webkit-line-clamp: 2;
		}
	}

	.co-4 {
		width: calc(25% - 30px);
	}

	.withBackgroundColor {
		background-color: inherit !important;
		border-color: rgba(1, 1, 1, 0.1) !important;
		a {
			color: #fff !important;
		}
	}

	.entry {
		display: block;
		flex-grow: 1;
		color: inherit;
		margin: 10px;
		background-color: #fff;
		color: #111;
		min-width: 235px;
		max-width: 600px;
		position: relative;

		.image {
			min-width: 235px;
			/* box ratio matches the requested image ratio so object-fit
			   never crops a second time on top of the server-side crop */
			aspect-ratio: 5 / 3;
			overflow: hidden;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		&.co-1 .image {
			aspect-ratio: 9 / 4;
		}

		.mainInfo {
			text-align: center;
			display: flex;
			justify-content: center;
			align-content: center;
			align-items: center;
			// height: 85px;
			padding: 15px 4px;
			text-align: center;
			position: relative;
			flex-wrap: wrap;

			.of {
				position: absolute;
				top: -10px;
				left: 5px;
				width: 100%;
				display: flex;
				justify-content: center;
			}

			.title {
				font-style: normal;
				font-weight: 700;
				color: #18283c;
				font-size: 18px;

				overflow: hidden;
				display: -webkit-box;
				-webkit-box-orient: vertical;
				text-overflow: hidden;
				white-space: normal;
				text-overflow: ellipsis;
				min-width: 100%;
				min-height: 48px;
				text-align: center !important;
			}

			.summary {
				overflow: hidden;
				display: -webkit-box;
				-webkit-box-orient: vertical;
				text-overflow: hidden;
				white-space: normal;
				text-overflow: ellipsis;
				width: calc(100% - 10px);
				margin: auto;
				padding: 5px;
				text-align: left;
				font-size: 16px;
				-webkit-line-clamp: 2;
				font-weight: 300;
				height: 48px;
			}

			.date {
				font-size: 14px;
				margin: 5px 0 5px 0;
				color: #888;
			}
		}

		.topic {
			background-color: #c80d11;
			color: #fff;
			text-align: center;
			padding: 0 2px 0 2px;
			display: inline-block;
			padding: 2px;
			text-transform: uppercase;
		}
	}

	:global {
		.carousel-news {
			div.entry {
				overflow: hidden;

				min-width: calc(
					var(--parent-width) / var(--parentColumns) - 20px
				) !important;

				a {
					-webkit-line-clamp: 2;
				}

				.summary {
					-webkit-line-clamp: 2;
				}
			}
		}
	}

	@media only screen and (max-width: 1000px) {
		.co-1 {
			width: calc(100% - 30px);
			max-width: 100%;
		}
	}

	@media only screen and (max-width: 650px) {
		.entry {
			.image {
				height: auto;
			}
		}

		.co-1 {
			width: calc(100% - 30px);
		}

		:global {
			.carousel-news {
				div.entry {
					min-width: calc(var(--parent-width) - 21px) !important;
				}
			}
		}
	}

	@media only screen and (max-width: 500px) {
		.entry {
			border-bottom: 4px solid #f1f1f1;
		}
	}
</style>
